import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Trophy, Loader2, ChevronRight } from 'lucide-react';
import api from '../lib/api';

const CATEGORY_META = {
  overall: { label: 'Overall', icon: '🏆' },
  hosting: { label: 'Hosting', icon: '🎉' },
  fitness: { label: 'Fitness', icon: '💪' },
  wellness: { label: 'Wellness', icon: '🧘' },
  coffee: { label: 'Coffee', icon: '☕' },
  food: { label: 'Food', icon: '🍣' },
  outdoors: { label: 'Outdoors', icon: '🥾' },
  travel: { label: 'Travel', icon: '✈️' },
  culture: { label: 'Culture', icon: '🎨' },
  social: { label: 'Social', icon: '🤝' },
};

export default function Leaderboard() {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const [category, setCategory] = useState(params.get('category') || 'overall');
  const [city, setCity] = useState(params.get('city') || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const q = new URLSearchParams();
        if (city) q.set('city', city);
        if (category) q.set('category', category);
        q.set('limit', '25');
        const r = await api.get(`/reputation/leaderboard?${q.toString()}`);
        if (alive) setData(r.data);
      } catch (e) { console.error(e); }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [city, category]);

  return (
    <div className="min-h-[100dvh] bg-[#F7F6F2]" data-testid="leaderboard-page">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-black/[0.04]">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} data-testid="lb-back" className="w-9 h-9 rounded-full bg-[#F1F0EA] flex items-center justify-center">
            <ArrowLeft size={16} />
          </button>
          <div className="flex-1">
            <h1 className="text-base font-extrabold text-[#111]">Leaderboard</h1>
            <p className="text-[11px] text-[#6B6E73]">
              {CATEGORY_META[category]?.label || 'Overall'} {city && `· ${city}`}
            </p>
          </div>
          <Trophy size={18} className="text-[#B6E600]" />
        </div>
        <div className="max-w-2xl mx-auto px-4 pb-3 flex gap-1.5 overflow-x-auto hide-scrollbar">
          {Object.entries(CATEGORY_META).map(([k, v]) => (
            <button
              key={k}
              onClick={() => setCategory(k)}
              data-testid={`lb-cat-${k}`}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-extrabold transition ${category === k ? 'bg-[#111] text-white' : 'bg-white text-[#6B6E73] border border-black/[0.06]'}`}
            >
              {v.icon} {v.label}
            </button>
          ))}
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-4 py-4">
        {loading && <div className="flex justify-center py-8"><Loader2 size={18} className="animate-spin text-[#9B9EA3]" /></div>}
        {!loading && data && (
          <>
            {data.me && (
              <div data-testid="lb-me" className="bg-[#B6E600]/15 border border-[#3F5200]/15 rounded-2xl p-3 mb-3 flex items-center gap-3">
                <div className="text-lg font-extrabold text-[#3F5200] w-8 text-right">#{data.me.rank}</div>
                <div className="flex-1">
                  <p className="text-sm font-extrabold text-[#111]">You</p>
                  <p className="text-[11px] text-[#6B6E73]">{data.me.points} pts in {CATEGORY_META[category]?.label}</p>
                </div>
                <span className="text-[10px] uppercase tracking-wider font-extrabold text-[#3F5200] bg-white px-2 py-1 rounded-full">
                  Top {data.total > 0 ? Math.max(1, Math.round(((data.me.rank || 1) / data.total) * 100)) : 100}%
                </span>
              </div>
            )}
            <div className="space-y-1.5" data-testid="lb-rows">
              {(data.top || []).map(row => (
                <div key={row.id} data-testid={`lb-row-${row.id}`} className="bg-white rounded-2xl border border-black/[0.04] p-2.5 flex items-center gap-3">
                  <div className={`w-7 text-center text-sm font-extrabold ${row.rank <= 3 ? 'text-[#B6E600]' : 'text-[#9B9EA3]'}`}>
                    {row.rank === 1 ? '🥇' : row.rank === 2 ? '🥈' : row.rank === 3 ? '🥉' : `#${row.rank}`}
                  </div>
                  {row.profile_photo ? (
                    <img loading="lazy" decoding="async" src={row.profile_photo} alt="" className="w-9 h-9 rounded-full object-cover" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-[#F1F0EA] flex items-center justify-center text-xs font-bold text-[#9B9EA3]">{(row.display_name || '?').slice(0, 2).toUpperCase()}</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-extrabold text-[#111] truncate">{row.display_name}</p>
                    <p className="text-[11px] text-[#9B9EA3]">@{row.username}{row.city && ` · ${row.city}`}</p>
                  </div>
                  <p className="text-sm font-extrabold text-[#111] tabular-nums">{row.display_pts} pts</p>
                </div>
              ))}
            </div>
            <p className="text-center text-[11px] text-[#9B9EA3] mt-4">
              Ranking out of {data.total} {city ? city : 'total'} {category !== 'overall' ? CATEGORY_META[category]?.label.toLowerCase() : ''} users
            </p>
          </>
        )}
      </main>
    </div>
  );
}
